import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { subYears, endOfMonth, format } from "date-fns";
import { TrendingUp, TrendingDown, Euro, FileText, Target, BarChart3 } from "lucide-react";
import { useDateRange } from "./DateRangeContext";
const AnalyticsSummaryCardsSkeleton = () => <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
    {[1, 2, 3, 4, 5].map((i) => <Card key={i}>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-24" />
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-4 w-16" />
          </div>
        </CardContent>
      </Card>)}
  </div>;
interface AnalyticsMetrics {
  sales: number;
  salesForGrowth: number;
  salesPrevious: number;
  arr: number;
  arrPrevious: number;
  proposalsCount: number;
  proposalsCountPrevious: number;
  proposalAvgValue: number;
  proposalAvgValuePrevious: number;
  successRate: number;
  successRatePrevious: number;
}
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('el-GR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};
const formatGrowth = (current: number, previous: number) => {
  // Don't show growth if previous is 0 or both values are 0
  if (previous === 0 || current === 0 && previous === 0) {
    return {
      percentage: 0,
      isPositive: true,
      hasData: false
    };
  }
  const growth = (current - previous) / previous * 100;
  return {
    percentage: Math.abs(growth),
    isPositive: growth >= 0,
    hasData: true
  };
};
export function AnalyticsSummaryCards() {
  const {
    dateRange
  } = useDateRange();
  const {
    data: metrics,
    isLoading,
    error
  } = useQuery<AnalyticsMetrics>({
    // v2: ARR now counts only projects active *today* (start_date <= today < delivered_date)
    queryKey: ["analytics-summary-v2", dateRange.from, dateRange.to],
    queryFn: async (): Promise<AnalyticsMetrics> => {
      try {
        console.log('AnalyticsSummaryCards: Starting queries...');
        console.log('AnalyticsSummaryCards: Date range:', {
          from: dateRange.from.toISOString(),
          to: dateRange.to.toISOString()
        });

        // Used for ARR (current active subscriptions)
        const today = new Date().toISOString().split('T')[0];

        // Calculate previous year's same period for comparison.
        // Use the EXACT selected date range mirrored to the previous year so the
        // displayed Sales total and the "vs YA" comparison cover identical month spans.
        const todayDate = new Date();
        const isCurrentYear = dateRange.from.getFullYear() === todayDate.getFullYear();
        const previousYearStart = new Date(dateRange.from.getFullYear() - 1, dateRange.from.getMonth(), 1);
        const previousYearEnd = endOfMonth(new Date(dateRange.to.getFullYear() - 1, dateRange.to.getMonth(), 1));
        const currentFrom = format(dateRange.from, 'yyyy-MM-dd');
        const currentTo = format(dateRange.to, 'yyyy-MM-dd');
        const previousFrom = format(previousYearStart, 'yyyy-MM-dd');
        const previousTo = format(previousYearEnd, 'yyyy-MM-dd');

        // Growth comparison uses ONLY fully completed months on both sides
        // (matches the Invoiced card in Sales Projection). If viewing the current
        // year, "completed" = months strictly before the current month.
        // For past years, use the full year on both sides.
        const selectedYear = dateRange.from.getFullYear();
        const completedMonths = selectedYear === todayDate.getFullYear()
          ? todayDate.getMonth() // 0-indexed: June (5) → 5 completed months (Jan–May)
          : 12;
        const growthCurrentFrom = format(new Date(selectedYear, 0, 1), 'yyyy-MM-dd');
        const growthCurrentTo = completedMonths > 0
          ? format(endOfMonth(new Date(selectedYear, completedMonths - 1, 1)), 'yyyy-MM-dd')
          : format(new Date(selectedYear, 0, 1), 'yyyy-MM-dd');
        const growthPrevFrom = format(new Date(selectedYear - 1, 0, 1), 'yyyy-MM-dd');
        const growthPrevTo = completedMonths > 0
          ? format(endOfMonth(new Date(selectedYear - 1, completedMonths - 1, 1)), 'yyyy-MM-dd')
          : format(new Date(selectedYear - 1, 0, 1), 'yyyy-MM-dd');
        const hasCompletedMonths = completedMonths > 0;

        console.log('AnalyticsSummaryCards: Comparison periods:', {
          displayRange: { from: currentFrom, to: currentTo },
          growthCurrent: { from: growthCurrentFrom, to: growthCurrentTo },
          growthPrevious: { from: growthPrevFrom, to: growthPrevTo },
          completedMonths
        });
        // Full previous year for Success Rate comparison
        const fullPrevYearStart = format(new Date(dateRange.from.getFullYear() - 1, 0, 1), 'yyyy-MM-dd');
        const fullPrevYearEnd = format(new Date(dateRange.from.getFullYear() - 1, 11, 31), 'yyyy-MM-dd');

        // End of previous year date for ARR comparison
        const endOfPrevYear = format(new Date(dateRange.from.getFullYear() - 1, 11, 31), 'yyyy-MM-dd');

        const [proposalsResult, invoicesResult, projectsResult, previousInvoicesResult, previousProposalsResult, fullPrevYearProposalsResult, arrEndOfPrevYearResult, growthCurrentInvoicesResult] = await Promise.all([
        supabase.from('proposals').select('value, final_value, sent_date, status').not('sent_date', 'is', null).gte('sent_date', currentFrom).lte('sent_date', currentTo).limit(500),
        supabase.from('invoices').select('amount, issue_date, status').not('issue_date', 'is', null).gte('issue_date', currentFrom).lte('issue_date', currentTo).limit(500),
        // ARR projects active today
        supabase.from('projects').select('arr, start_date, delivered_date, value, sold_for, extra_value, status').eq('arr', true).gt('delivered_date', today).not('start_date', 'is', null).lte('start_date', today).not('status', 'in', '("rejected","cancelled","lost")'),
        // Previous period invoices for growth comparison — completed months only
        supabase.from('invoices').select('amount, issue_date, status').not('issue_date', 'is', null).gte('issue_date', growthPrevFrom).lte('issue_date', growthPrevTo).limit(500),
        // Previous period proposals for comparison (mirrored selected range)
        supabase.from('proposals').select('value, final_value, sent_date, status').not('sent_date', 'is', null).gte('sent_date', previousFrom).lte('sent_date', previousTo).limit(500),
        // Full previous year proposals for Success Rate comparison
        supabase.from('proposals').select('sent_date, status').not('sent_date', 'is', null).gte('sent_date', fullPrevYearStart).lte('sent_date', fullPrevYearEnd).limit(500),
        // ARR projects active as of end of previous year (start_date <= Dec 31 AND delivered_date > Dec 31)
        supabase.from('projects').select('arr, start_date, delivered_date, value, sold_for, extra_value, status').eq('arr', true).gt('delivered_date', endOfPrevYear).not('start_date', 'is', null).lte('start_date', endOfPrevYear).not('status', 'in', '("rejected","cancelled","lost")'),
        // Current year invoices restricted to completed months (for growth comparison)
        supabase.from('invoices').select('amount, issue_date, status').not('issue_date', 'is', null).gte('issue_date', growthCurrentFrom).lte('issue_date', growthCurrentTo).limit(500)]
        );
        console.log('AnalyticsSummaryCards: Query results:', {
          proposalsError: proposalsResult.error,
          proposalsCount: proposalsResult.data?.length,
          invoicesError: invoicesResult.error,
          invoicesCount: invoicesResult.data?.length,
          projectsError: projectsResult.error,
          projectsCount: projectsResult.data?.length,
          previousInvoicesCount: previousInvoicesResult.data?.length,
          previousProposalsCount: previousProposalsResult.data?.length
        });
        if (proposalsResult.error) {
          console.error('AnalyticsSummaryCards: Proposals query error:', proposalsResult.error);
          throw proposalsResult.error;
        }
        if (invoicesResult.error) {
          console.error('AnalyticsSummaryCards: Invoices query error:', invoicesResult.error);
          throw invoicesResult.error;
        }
        if (projectsResult.error) {
          console.error('AnalyticsSummaryCards: Projects query error:', projectsResult.error);
          throw projectsResult.error;
        }
        const proposals = proposalsResult.data || [];
        const invoices = invoicesResult.data || [];
        const projects = projectsResult.data || [];
        const previousInvoices = previousInvoicesResult.data || [];
        const previousProposals = previousProposalsResult.data || [];
        const fullPrevYearProposals = fullPrevYearProposalsResult.data || [];
        console.log('AnalyticsSummaryCards: Processing data:', {
          proposalsData: proposals.slice(0, 2),
          invoicesData: invoices.slice(0, 2),
          projectsData: projects.slice(0, 2),
          previousInvoicesData: previousInvoices.slice(0, 2),
          previousProposalsData: previousProposals.slice(0, 2)
        });
        console.log('AnalyticsSummaryCards: Processing data:', {
          proposalsData: proposals.slice(0, 2),
          invoicesData: invoices.slice(0, 2),
          projectsData: projects.slice(0, 2)
        });

        // Simple calculations - count all issued invoices (same logic as Sales Projection "Invoiced so far")
        const sales = invoices.filter((inv) => inv.issue_date) // Only invoices that have been issued
        .reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0);

        // Growth comparison uses completed full months on both sides
        const growthCurrentInvoices = growthCurrentInvoicesResult.data || [];
        const salesForGrowth = hasCompletedMonths
          ? growthCurrentInvoices.filter((inv) => inv.issue_date).reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0)
          : 0;

        // Previous year sales for the SAME completed-month window
        const salesPrevious = hasCompletedMonths
          ? previousInvoices.filter((inv) => inv.issue_date).reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0)
          : 0;
        console.log('📊 Analytics Summary Card - Sales calculation:', {
          sales,
          salesPrevious,
          growth: ((sales - salesPrevious) / salesPrevious * 100).toFixed(1),
          invoicesCount: invoices.length,
          previousInvoicesCount: previousInvoices.length,
          dateRange: {
            current: {
              from: currentFrom,
              to: currentTo
            },
            previous: {
              from: previousFrom,
              to: previousTo
            }
          },
          currentPeriodInvoices: invoices.map((i) => ({
            amount: i.amount,
            issue_date: i.issue_date
          })).slice(0, 5),
          previousPeriodInvoices: previousInvoices.map((i) => ({
            amount: i.amount,
            issue_date: i.issue_date
          })).slice(0, 5)
        });

        // ARR calculation (projectsResult is already filtered to active projects for today)
        const arr = projects.reduce((sum, project) => {
          const projectValue = Number(project.sold_for) || Number(project.value) || 0;
          const extraValue = Number(project.extra_value) || 0;
          return sum + projectValue + extraValue;
        }, 0);

        // ARR at end of previous year for comparison
        const arrPrevYearProjects = arrEndOfPrevYearResult.data || [];
        const arrPrevious = arrPrevYearProjects.reduce((sum, project) => {
          const projectValue = Number(project.sold_for) || Number(project.value) || 0;
          const extraValue = Number(project.extra_value) || 0;
          return sum + projectValue + extraValue;
        }, 0);
        console.log('AnalyticsSummaryCards: ARR calculation:', {
          arr,
          projectsCount: projects.length
        });
        const proposalsCount = proposals.filter((p) => p.sent_date).length;
        const proposalsCountPrevious = previousProposals.filter((p) => p.sent_date).length;

        // Calculate success rate (accepted proposals / total sent proposals)
        const sentProposals = proposals.filter((p) => p.sent_date);
        const acceptedCount = sentProposals.filter((p) => p.status === 'accepted').length;
        const successRate = sentProposals.length > 0 ? acceptedCount / sentProposals.length * 100 : 0;

        // Full previous year success rate (for Success Rate card comparison)
        const fullPrevYearSent = fullPrevYearProposals.filter((p) => p.sent_date);
        const fullPrevYearAccepted = fullPrevYearSent.filter((p) => p.status === 'accepted').length;
        const successRatePrevious = fullPrevYearSent.length > 0 ? fullPrevYearAccepted / fullPrevYearSent.length * 100 : 0;
        const totalValue = sentProposals.reduce((sum, p) => sum + (Number(p.final_value || p.value) || 0), 0);
        const proposalAvgValue = sentProposals.length > 0 ? totalValue / sentProposals.length : 0;

        // Previous period average proposal value
        const previousSentProposals = previousProposals.filter((p) => p.sent_date);
        const previousTotalValue = previousSentProposals.reduce((sum, p) => sum + (Number(p.final_value || p.value) || 0), 0);
        const proposalAvgValuePrevious = previousSentProposals.length > 0 ? previousTotalValue / previousSentProposals.length : 0;
        console.log('AnalyticsSummaryCards: Final metrics:', {
          sales,
          arr,
          proposalsCount,
          successRate,
          proposalAvgValue
        });
        return {
          sales,
          salesForGrowth,
          salesPrevious,
          arr,
          arrPrevious,
          proposalsCount,
          proposalsCountPrevious,
          proposalAvgValue,
          proposalAvgValuePrevious,
          successRate,
          successRatePrevious
        };
      } catch (err) {
        console.error('AnalyticsSummaryCards: Query error:', err);
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000,
    // 5 minutes
    retry: 2,
    // Only retry twice
    retryDelay: 1000
  });
  if (isLoading) {
    return <AnalyticsSummaryCardsSkeleton />;
  }
  if (error) {
    return <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">
              Failed to load analytics
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  if (!metrics) return null;
  const salesGrowth = formatGrowth(metrics.salesForGrowth, metrics.salesPrevious);
  const arrGrowth = formatGrowth(metrics.arr, metrics.arrPrevious);
  const proposalsGrowth = formatGrowth(metrics.proposalsCount, metrics.proposalsCountPrevious);
  const successRateGrowth = formatGrowth(metrics.successRate || 0, metrics.successRatePrevious || 0);
  const avgValueGrowth = formatGrowth(metrics.proposalAvgValue, metrics.proposalAvgValuePrevious);
  return <div className="grid grid-cols-2 md:grid-cols-5 gap-4 animate-fade-in">
      {/* Sales */}
      <Card className="hover-scale">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Euro className="h-4 w-4 text-primary" />
            Sales
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            <div className="text-2xl font-bold">
              {formatCurrency(metrics.sales)}
            </div>
            {salesGrowth.hasData && <div className={`flex items-center gap-1 text-sm ${salesGrowth.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {salesGrowth.isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {salesGrowth.percentage.toFixed(1)}% vs YA
              </div>}
          </div>
        </CardContent>
      </Card>

      {/* ARR */}
      <Card className="hover-scale">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            ARR
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            <div className="text-2xl font-bold">
              {formatCurrency(metrics.arr)}
            </div>
            {(metrics.arr > 0 || metrics.arrPrevious > 0) && <div className={`flex items-center gap-1 text-sm ${metrics.arr >= metrics.arrPrevious ? 'text-green-600' : 'text-red-600'}`}>
                {metrics.arr >= metrics.arrPrevious ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {metrics.arr - metrics.arrPrevious >= 0 ? '+' : ''}{formatCurrency(metrics.arr - metrics.arrPrevious)} vs End of {dateRange.from.getFullYear() - 1}
              </div>}
          </div>
        </CardContent>
      </Card>

      {/* Proposals Count */}
      <Card className="hover-scale">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Proposals
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            <div className="text-2xl font-bold">
              {metrics.proposalsCount}
            </div>
            {metrics.proposalsCountPrevious !== undefined && <div className={`flex items-center gap-1 text-sm ${metrics.proposalsCount >= metrics.proposalsCountPrevious ? 'text-green-600' : 'text-red-600'}`}>
                {metrics.proposalsCount >= metrics.proposalsCountPrevious ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {metrics.proposalsCount - metrics.proposalsCountPrevious >= 0 ? '+' : ''}{metrics.proposalsCount - metrics.proposalsCountPrevious} vs YA
              </div>}
          </div>
        </CardContent>
      </Card>

      {/* Success Rate */}
      <Card className="hover-scale">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" />
            Success Rate
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            <div className="text-2xl font-bold">
              {(metrics.successRate || 0).toFixed(1)}%
            </div>
            {(metrics.successRate > 0 || metrics.successRatePrevious > 0) && (() => {
              const diff = (metrics.successRate || 0) - (metrics.successRatePrevious || 0);
              return <div className={`flex items-center gap-1 text-sm ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {diff >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {diff >= 0 ? '+' : ''}{diff.toFixed(1)}pp vs Total {dateRange.from.getFullYear() - 1}
              </div>;
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Proposal Average Value */}
      <Card className="hover-scale">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" />
            Av. Proposal Value
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            <div className="text-2xl font-bold">
              {formatCurrency(metrics.proposalAvgValue)}
            </div>
            {metrics.proposalAvgValuePrevious !== undefined && (metrics.proposalAvgValue > 0 || metrics.proposalAvgValuePrevious > 0) && <div className={`flex items-center gap-1 text-sm ${metrics.proposalAvgValue >= metrics.proposalAvgValuePrevious ? 'text-green-600' : 'text-red-600'}`}>
                {metrics.proposalAvgValue >= metrics.proposalAvgValuePrevious ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {metrics.proposalAvgValue - metrics.proposalAvgValuePrevious >= 0 ? '+' : ''}{formatCurrency(metrics.proposalAvgValue - metrics.proposalAvgValuePrevious)} vs YA
              </div>}
          </div>
        </CardContent>
      </Card>

    </div>;}